/**
 * Volume envelope for `@ad-studio/audio` and `@ad-studio/video` diegetic
 * sound. Fades are authored in seconds (what people hear) and converted to
 * frames with the composition fps so a 0.5s fade stays 0.5s if fps changes.
 *
 * Remotion's volume callback already reports visible-layer time: it adds
 * `cumulatedNegativeFrom` back onto `useCurrentFrame()`, so frame 0 is the
 * first visible frame inside the trim Sequence. Do not subtract the trim
 * in-point again.
 */

/** Fade lengths in frames, already clamped to the clip they sit on. */
export type ResolvedFade = {
  fadeInFrames: number;
  fadeOutFrames: number;
};

const NO_FADE: ResolvedFade = { fadeInFrames: 0, fadeOutFrames: 0 };

/** Peak gain when `volume` is omitted or not a finite number. Remotion's default. */
export const DEFAULT_MEDIA_VOLUME = 1;

function readSeconds(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return 0;
  }
  return value;
}

export function peakMediaVolume(volume: unknown): number {
  if (typeof volume !== "number" || !Number.isFinite(volume) || volume < 0) {
    return DEFAULT_MEDIA_VOLUME;
  }
  return volume;
}

export function fadeFramesToSeconds(frames: number, fps: number): number {
  return fps > 0 ? frames / fps : 0;
}

/**
 * Fades that together outlast the clip are scaled down proportionally until
 * they just meet: overlapping them multiplies the two ramps into an audible dip
 * in the middle. Stored values are left alone, so trimming a clip short and
 * pulling it back out restores the fade that was set.
 */
export function resolveFadeFrames(
  props: { fadeInSeconds?: unknown; fadeOutSeconds?: unknown },
  { durationInFrames, fps }: { durationInFrames: number; fps: number },
): ResolvedFade {
  if (
    !Number.isFinite(durationInFrames) ||
    durationInFrames <= 0 ||
    !Number.isFinite(fps) ||
    fps <= 0
  ) {
    return NO_FADE;
  }

  let fadeInFrames = readSeconds(props.fadeInSeconds) * fps;
  let fadeOutFrames = readSeconds(props.fadeOutSeconds) * fps;
  const total = fadeInFrames + fadeOutFrames;
  if (total > durationInFrames) {
    const scale = durationInFrames / total;
    fadeInFrames *= scale;
    fadeOutFrames *= scale;
  }
  return { fadeInFrames, fadeOutFrames };
}

/**
 * Equal-power ramp, -3dB at its midpoint: a fade from silence moves at a
 * constant perceived rate, where a linear gain ramp reads as a cut with a tail.
 */
function equalPowerGain(progress: number): number {
  return Math.sin(Math.min(1, Math.max(0, progress)) * (Math.PI / 2));
}

/**
 * Remotion `volume` prop: a constant when there is no fade, otherwise a
 * per-frame callback in visible-layer time.
 */
export function remotionMediaVolume({
  durationInFrames,
  fps,
  volume,
  fadeInSeconds,
  fadeOutSeconds,
}: {
  durationInFrames: number | undefined;
  fps: number;
  volume?: unknown;
  fadeInSeconds?: unknown;
  fadeOutSeconds?: unknown;
}): number | ((frame: number) => number) {
  const peak = peakMediaVolume(volume);
  if (durationInFrames == null || durationInFrames < 1) {
    return peak;
  }
  const { fadeInFrames, fadeOutFrames } = resolveFadeFrames(
    { fadeInSeconds, fadeOutSeconds },
    { durationInFrames, fps },
  );
  if (fadeInFrames === 0 && fadeOutFrames === 0) {
    return peak;
  }
  return (frame) => {
    if (frame < 0) return 0;
    if (frame >= durationInFrames) return fadeOutFrames > 0 ? 0 : peak;
    let gain = peak;
    if (fadeInFrames > 0) {
      gain *= equalPowerGain(frame / fadeInFrames);
    }
    if (fadeOutFrames > 0) {
      gain *= equalPowerGain((durationInFrames - frame) / fadeOutFrames);
    }
    return gain;
  };
}
